import { ThumbnailMetrics } from 'Roblox';
import { BatchRequestError, QueueItem } from 'core-utilities';
import { dataStores } from 'core-roblox-utilities';
import thumbnailRequester, { idSerializer } from '../util/thumbnailRequester';

import {
  ThumbnailTypes,
  ThumbnailAssetsSize,
  ThumbnailGameIconSize,
  ThumbnailGamePassIconSize,
  ThumbnailGameThumbnailSize,
  ThumbnailGroupIconSize,
  ThumbnailBadgeIconSize,
  ThumbnailDeveloperProductIconSize,
  ThumbnailAvatarsSize,
  ThumbnailStates,
  ThumbnailFormat,
  ReturnPolicy,
  ThumbnailPendingTracker,
  ThumbnailDataData,
  ThumbnailDataItem,
  ThumbnailAvatarHeadshotSize
} from '../constants/thumbnail2dConstant';

const { processThumbnailBatchRequest } = thumbnailRequester;

const thumbnailPendingTracker: ThumbnailPendingTracker = {};

const { thumbnailsDataStore, gameThumbnailsDataStore } = dataStores;

const dataStoreInstanceMap = {
  [ThumbnailTypes.assetThumbnail]: thumbnailsDataStore.getAssets,
  [ThumbnailTypes.avatarHeadshot]: thumbnailsDataStore.getAvatarHeadshots,
  [ThumbnailTypes.avatar]: thumbnailsDataStore.getAvatars,
  [ThumbnailTypes.bundleThumbnail]: thumbnailsDataStore.getBundles,
  [ThumbnailTypes.gameIcon]: gameThumbnailsDataStore.getIcons,
  [ThumbnailTypes.groupIcon]: thumbnailsDataStore.getGroupIcons,
  [ThumbnailTypes.badgeIcon]: thumbnailsDataStore.getBadgeIcons,
  [ThumbnailTypes.gamePassIcon]: gameThumbnailsDataStore.getGamePassIcons,
  [ThumbnailTypes.developerProductIcon]: thumbnailsDataStore.getDeveloperProductIcons,
  [ThumbnailTypes.userOutfit]: thumbnailsDataStore.getUserOutfits,
  [ThumbnailTypes.gameThumbnail]: gameThumbnailsDataStore.getThumbnailsByThumbnailIds
};

const getDataStoreInstance = (thumbnailType: ThumbnailTypes) => {
  const dataStoreInstance = dataStoreInstanceMap[thumbnailType];
  if (!dataStoreInstance) {
    console.debug(`Missing urlConfig implementation for thumbnail type: ${thumbnailType}`);
  }
  return dataStoreInstance;
};

const getThumbnailCacheKey = (
  thumbnailType: ThumbnailTypes,
  baseTargetId: number,
  thumbnailTargetId: number,
  requestedSize:
    | ThumbnailAssetsSize
    | ThumbnailGameIconSize
    | ThumbnailGameThumbnailSize
    | ThumbnailGamePassIconSize
    | ThumbnailAvatarsSize
    | ThumbnailAvatarHeadshotSize
    | ThumbnailGroupIconSize
    | ThumbnailBadgeIconSize
    | ThumbnailDeveloperProductIconSize,
  format: ThumbnailFormat = ThumbnailFormat.png
): string => {
  return `thumbnail_type:${thumbnailType}_baseTargetId:${baseTargetId}_targetId:${thumbnailTargetId}_requestedSize:${requestedSize}_format:${format}`;
};
const getThumbnailRequesterKey = (
  baseTargetId: number,
  thumbnailType: ThumbnailTypes,
  size:
    | ThumbnailAssetsSize
    | ThumbnailGameIconSize
    | ThumbnailGameThumbnailSize
    | ThumbnailGamePassIconSize
    | ThumbnailAvatarsSize
    | ThumbnailAvatarHeadshotSize
    | ThumbnailGroupIconSize
    | ThumbnailBadgeIconSize
    | ThumbnailDeveloperProductIconSize,
  format: ThumbnailFormat = ThumbnailFormat.png
) => {
  return `${baseTargetId}:${thumbnailType}:${size}:${format}`;
};

const handleBatchRequest = (
  baseTargetId: number,
  items: Array<QueueItem>,
  thumbnailType: ThumbnailTypes,
  size:
    | ThumbnailAssetsSize
    | ThumbnailGameIconSize
    | ThumbnailGameThumbnailSize
    | ThumbnailGamePassIconSize
    | ThumbnailAvatarsSize
    | ThumbnailAvatarHeadshotSize
    | ThumbnailGroupIconSize
    | ThumbnailBadgeIconSize
    | ThumbnailDeveloperProductIconSize,
  format: ThumbnailFormat = ThumbnailFormat.png
) => {
  const thumbnailDataStoreInstance = getDataStoreInstance(thumbnailType);
  return new Promise((resolve, reject) => {
    // --- remove after rollout corescript
    let ids = items;
    const isCoreScriptDeployed = !!items[0]?.itemId; // @ts-ignore
    if (isCoreScriptDeployed) {
      // @ts-ignore
      ids = items.map(({ itemId }) => itemId);
    }
    // --- remove after rollout corescript

    if (!thumbnailDataStoreInstance) {
      reject(BatchRequestError.unretriableFailure);
      return;
    }

    const requestStartTime = new Date().getTime();
    let dataStoreArgs: Array<any> = [];
    if (
      thumbnailType === ThumbnailTypes.assetThumbnail ||
      thumbnailType === ThumbnailTypes.gameIcon
    ) {
      dataStoreArgs = [ids, ReturnPolicy.PlaceHolder, size, format];
    } else if (thumbnailType === ThumbnailTypes.gameThumbnail) {
      dataStoreArgs = [baseTargetId, ids, size, format];
    } else {
      dataStoreArgs = [ids, size, format];
    }

    thumbnailDataStoreInstance
      .apply(null, dataStoreArgs)
      .then((thumbnailData: ThumbnailDataData) => {
        const results: { [key: string]: ThumbnailDataItem } = {};
        const itemsData = thumbnailData?.data?.data ?? [];
        itemsData.forEach((item: ThumbnailDataItem) => {
          results[idSerializer(item.targetId)] = item;
        });
        items.forEach(item => {
          // --- remove after rollout corescript
          let id = 0;
          let startTime;
          if (isCoreScriptDeployed) {
            id = item.itemId;
            startTime = item.startTime;
          } else {
            // @ts-ignore
            id = item;
          }
          // ---- remove after rollout corescript
          const key = idSerializer(id);

          if (Object.prototype.hasOwnProperty.call(results, key) && results[key]) {
            // Removing it from the results we resolve with will make sure it gets retried.
            if (results[key].state === ThumbnailStates.pending) {
              delete results[key];
              if (!thumbnailPendingTracker[key]) {
                thumbnailPendingTracker[key] = { startTime: requestStartTime };
              }
            } else if (results[key].state === ThumbnailStates.complete) {
              const requestCompleteTime = new Date().getTime();

              // measure each retry load time.
              if (thumbnailPendingTracker[key] && ThumbnailMetrics) {
                ThumbnailMetrics.logFinalThumbnailTime(
                  requestCompleteTime - thumbnailPendingTracker[key].startTime
                );
                delete thumbnailPendingTracker[key];
              }
              if (startTime && ThumbnailMetrics) {
                // measure end to end load time include retry.
                ThumbnailMetrics.logFinalThumbnailTime(requestCompleteTime - startTime.getTime());
              }
            }
          } else {
            results[key] = {
              targetId: id,
              state: ThumbnailStates.error
            };
          }
        });

        resolve(results);
      })
      .catch(() => {
        // No success cases this time, all will be retried.
        resolve({});
      });
  });
};

const loadThumbnailImage = (
  thumbnailType: ThumbnailTypes,
  baseTargetId: number,
  targetId: number,
  size:
    | ThumbnailAssetsSize
    | ThumbnailGameIconSize
    | ThumbnailGameThumbnailSize
    | ThumbnailGamePassIconSize
    | ThumbnailAvatarsSize
    | ThumbnailAvatarHeadshotSize
    | ThumbnailGroupIconSize
    | ThumbnailBadgeIconSize
    | ThumbnailDeveloperProductIconSize,
  format: ThumbnailFormat = ThumbnailFormat.png,
  clearCachedValue?: boolean
) => {
  if (!targetId) {
    return new Promise((resolve, reject) => {
      reject('TargetId can not be empty.');
    });
  }

  if (!thumbnailType) {
    return new Promise((resolve, reject) => {
      reject('ThumbnailType can not be empty.');
    });
  }

  const thumbnailCacheKey = getThumbnailCacheKey(
    thumbnailType,
    baseTargetId,
    targetId,
    size,
    format
  );
  const thumbnailRequesterKey = getThumbnailRequesterKey(baseTargetId, thumbnailType, size, format);

  return processThumbnailBatchRequest(
    targetId,
    (items: Array<QueueItem>) =>
      handleBatchRequest(baseTargetId, items, thumbnailType, size, format),
    thumbnailRequesterKey,
    thumbnailCacheKey,
    clearCachedValue
  );
};

const getThumbnailImage = (
  thumbnailType: ThumbnailTypes,
  targetId: number,
  size:
    | ThumbnailAssetsSize
    | ThumbnailGameIconSize
    | ThumbnailGameThumbnailSize
    | ThumbnailGamePassIconSize
    | ThumbnailAvatarsSize
    | ThumbnailAvatarHeadshotSize
    | ThumbnailGroupIconSize
    | ThumbnailBadgeIconSize
    | ThumbnailDeveloperProductIconSize,
  format: ThumbnailFormat = ThumbnailFormat.png
) => {
  return loadThumbnailImage(thumbnailType, null, targetId, size, format);
};

const reloadThumbnailImage = (
  thumbnailType: ThumbnailTypes,
  targetId: number,
  size:
    | ThumbnailAssetsSize
    | ThumbnailGameIconSize
    | ThumbnailGameThumbnailSize
    | ThumbnailGamePassIconSize
    | ThumbnailAvatarsSize
    | ThumbnailAvatarHeadshotSize
    | ThumbnailGroupIconSize
    | ThumbnailBadgeIconSize
    | ThumbnailDeveloperProductIconSize,
  format: ThumbnailFormat = ThumbnailFormat.png
) => {
  return loadThumbnailImage(thumbnailType, null, targetId, size, format, true);
};

const getThumbnailImageWithBaseTarget = (
  thumbnailType: ThumbnailTypes,
  baseTargetId: number,
  targetId: number,
  size:
    | ThumbnailAssetsSize
    | ThumbnailGameIconSize
    | ThumbnailGameThumbnailSize
    | ThumbnailGamePassIconSize
    | ThumbnailAvatarsSize
    | ThumbnailAvatarHeadshotSize
    | ThumbnailGroupIconSize
    | ThumbnailBadgeIconSize
    | ThumbnailDeveloperProductIconSize,
  format: ThumbnailFormat = ThumbnailFormat.png
) => {
  return loadThumbnailImage(thumbnailType, baseTargetId, targetId, size, format);
};

const getCssClass = (thumbnailState: ThumbnailStates) => {
  return {
    'icon-broken': thumbnailState === ThumbnailStates.error,
    'icon-in-review': thumbnailState === ThumbnailStates.inReview,
    'icon-blocked': thumbnailState === ThumbnailStates.blocked,
    'icon-pending': thumbnailState === ThumbnailStates.pending
  };
};

export default {
  getThumbnailImage,
  getCssClass,
  reloadThumbnailImage,
  getThumbnailImageWithBaseTarget
};
